import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { Router } from '@angular/router';
import { MOCDashboardService } from 'app/modules/management-of-change/services/moc-dashboard.service';
import module from 'app/modules/management-of-change/moc.module';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'app-moc-dashboard',
    templateUrl: './moc-dashboard.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./moc-dashboard.page.scss'],
})
export class MocDashboardPage {
    mocFieldSummary$ = this.mocDashboardService.mocFieldSummary$;

    public mocStatus: any;
    public keyIssueCategoriesView: Array<{ category: string; count: number }> =
        [];

    constructor(
        private elementRef: ElementRef,
        private mocDashboardService: MOCDashboardService,
        public router: Router,
    ) {}

    ngOnInit(): void {
        this.mocFieldSummary$.subscribe((res) => {
            if (res && res.data.data[0].mocStatus) {
                this.mocStatus = res.data.data[0].mocStatus;
                this.keyIssueCategoriesView = (
                    this.mocStatus?.keyIssueCategories ?? []
                )
                    .map((item: any) => ({
                        category: item?.category ?? 'Uncategorized',
                        count: Number(item?.count ?? 0),
                    }))
                    .sort((a: any, b: any) =>
                        a.category.localeCompare(b.category),
                    );
                this.createPieChart();
            }
        });
    }
    private createPieChart() {
        const chart = am4core.create('mocStatusChart', am4charts.PieChart);
        chart.padding(0, 0, 0, 0);
        chart.width = am4core.percent(100);
        chart.height = am4core.percent(100);

        chart.data = [
            {
                category: 'Active',
                value: this.mocStatus.active,
                color: am4core.color('#7ed77e'),
            },
            {
                category: 'Pending',
                value: this.mocStatus.pendingIssuance,
                color: am4core.color('#FBBF24'),
            },
            {
                category: 'Expired',
                value: this.mocStatus.expired,
                color: am4core.color('#EF4444'),
            },
        ];

        const pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = 'value';
        pieSeries.dataFields.category = 'category';
        pieSeries.slices.template.stroke = am4core.color('#fff');
        pieSeries.slices.template.strokeWidth = 2;
        pieSeries.slices.template.strokeOpacity = 1;
        pieSeries.slices.template.propertyFields.fill = 'color';

        // Donut
        pieSeries.innerRadius = am4core.percent(60);
        pieSeries.radius = am4core.percent(100);
        pieSeries.labels.template.disabled = true;
        pieSeries.labels.template.fill = am4core.color('#000');
        pieSeries.ticks.template.disabled = true;

        pieSeries.hiddenState.properties.opacity = 1;
        pieSeries.hiddenState.properties.endAngle = -90;
        pieSeries.hiddenState.properties.startAngle = -90;

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;
        chart.legend.valueLabels.template.disabled = true;

        // Custom marker style
        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        let markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;

        const legendLabel = chart.legend.labels.template;
        legendLabel.fontSize = 12;
        legendLabel.wrap = true;
        legendLabel.maxWidth = 80;
        legendLabel.text = '{category} ({value.percent.formatNumber("#.0")}%)';
        chart.legend.valueLabels.template.disabled = true;

        // --- CENTER LABEL (works) ---
        const total = this.mocStatus.active + this.mocStatus.closed;

        const label = chart.seriesContainer.createChild(am4core.Label);
        label.text = `[font-size:12px]Total[/]\n[bold font-size:18px]${total}[/]`;
        label.textAlign = 'middle';
        label.horizontalCenter = 'middle';
        label.verticalCenter = 'middle';
        // ignore layout so it can sit exactly in the middle
        label.isMeasured = false;
        label.x = am4core.percent(50);
        label.y = am4core.percent(50);
        label.fill = am4core.color('#000');
        // keep label correct if data changes
        pieSeries.events.on('validated', () => {
            const t = chart.data.reduce((s: number, d: any) => s + +d.value, 0);
            label.text = `[font-size:12px]Total[/]\n[bold font-size:18px]${t}[/]`;
        });

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    toggleSummary(routes: any) {
        this.router.navigate([`/wim/moc-dashboard/${routes}`]);
    }

    trackByCategory(index: number, item: { category: string; count: number }) {
        return item?.category ?? index;
    }
}
